import { useQuery } from 'react-query'
import useReceivedMacMinis from './use-received-mac-minis'
import useReceivedPhysicalPhones from './use-received-physical-phones'

interface NextDeviceNamesParams {
  terminalId: string
  terminalName: string
  deviceType: 'mac-mini' | 'physical-phone'
  count: number
}

// Helper function to extract device number from device name
const extractDeviceNumber = (
  name: string,
  deviceType: 'mac-mini' | 'physical-phone'
): number => {
  if (deviceType === 'mac-mini') {
    // Extract from A0M1 -> 1, A0M10 -> 10
    const match = name.match(/M(\d+)$/)
    return match ? parseInt(match[1], 10) : 0
  }
  // Extract from A0M0P1 -> 1, A0M0P10 -> 10
  const match = name.match(/P(\d+)$/)
  return match ? parseInt(match[1], 10) : 0
}

// Helper function to find highest device number from received devices
const findHighestReceivedDeviceNumber = (
  devices: any[],
  deviceType: 'mac-mini' | 'physical-phone'
): number => {
  if (!devices || devices.length === 0) return 0

  const numbers = devices
    .filter((device) => device.status === 'RECEIVED' && !device.ansibleId) // Only RECEIVED devices without ansible
    .map((device) => extractDeviceNumber(device.name, deviceType))
    .filter((num) => num > 0) // Only valid numbers

  return numbers.length > 0 ? Math.max(...numbers) : 0
}

// Utility function to generate names based on highest RECEIVED device number
export const generateReceivedDeviceNames = (
  terminalName: string,
  deviceType: 'mac-mini' | 'physical-phone',
  highestReceivedNumber: number,
  count: number
): string[] => {
  const names: string[] = []
  const terminalPart = terminalName === 'T000' ? '' : terminalName

  for (let i = 0; i < count; i++) {
    const deviceNumber = highestReceivedNumber + i + 1 // Start from highest + 1

    if (deviceType === 'mac-mini') {
      // Format: A0M1, A0M2, etc. (T000 omitted)
      names.push(`${terminalPart}A0M${deviceNumber}`)
    } else {
      // Format: A0M0P1, A0M0P2, etc. (T000 omitted)
      names.push(`${terminalPart}A0M0P${deviceNumber}`)
    }
  }

  return names
}

// Hook to get next device names based on highest RECEIVED device number
export const useNextDeviceNames = (params: NextDeviceNamesParams) => {
  // Fetch received devices based on device type
  const macMinisQuery = useReceivedMacMinis({
    terminalId: params.terminalId,
    limit: 100, // Get enough to find highest number
  })

  const phonesQuery = useReceivedPhysicalPhones({
    terminalId: params.terminalId,
    limit: 100, // Get enough to find highest number
  })

  const isEnabled =
    !!params.terminalId && !!params.terminalName && params.count > 0
  const relevantQuery =
    params.deviceType === 'mac-mini' ? macMinisQuery : phonesQuery

  return useQuery(
    ['next-device-names', params],
    () => {
      const devices = relevantQuery.data?.data || []
      const highestNumber = findHighestReceivedDeviceNumber(
        devices,
        params.deviceType
      )

      const names = generateReceivedDeviceNames(
        params.terminalName,
        params.deviceType,
        highestNumber,
        params.count
      )

      return {
        names,
        startingNumber: highestNumber + 1,
        highestReceivedNumber: highestNumber,
      }
    },
    {
      enabled: isEnabled && !relevantQuery.isLoading && !!relevantQuery.data,
      staleTime: 0, // Always fresh for naming
      retry: 2,
    }
  )
}
